import React, { useState, useEffect } from 'react';
import Header from '../components/header/Header.jsx';
import Footer from '../components/footer/Footer.jsx';
import { getAdminData } from '../services/apiService';

const STATUS_UPDATE_API_ENDPOINT = 'https://z50uovki66.execute-api.eu-west-2.amazonaws.com/prod/status';

const Notification = ({ message, type, onClose }) => {
    const baseStyle = "fixed top-5 right-5 p-4 rounded-lg shadow-lg text-white transition-opacity duration-300 z-50";
    const typeStyle = type === 'success' ? 'bg-green-500' : 'bg-red-500';
    return <div className={`${baseStyle} ${typeStyle}`}><span>{message}</span><button onClick={onClose} className="ml-4 font-bold">X</button></div>;
};

const StatusDropdown = ({ item, itemType, onStatusChange, showNotification }) => {
    const initialStatus = itemType === 'Company' ? item.companyStatus : item.userApprovalStatus;
    const [status, setStatus] = useState(initialStatus);
    const [loading, setLoading] = useState(false);

    const handleStatusChange = async (newStatus) => {
        setLoading(true);
        setStatus(newStatus);

        const itemName = itemType === 'Company' ? item.companyName : item.fullName;
        let requestBody;

        // --- NEW: Logic to build the correct request body based on the schema ---
        if (itemType === 'Company') {
            requestBody = {
                itemType: 'Company',
                id: item.companyId,
                status: newStatus
            };
        } else { // itemType is 'User'
            // The user's PK is the company they belong to, e.g., "COMPANY#c1"
            // We must extract "c1" from it.
            const companyId = item.PK.split('#')[1];

            if (!companyId) {
                showNotification(`Could not determine company for user '${itemName}'. Malformed PK.`, 'error');
                setStatus(initialStatus);
                setLoading(false);
                return;
            }

            requestBody = {
                itemType: 'User',
                userId: item.userId,
                companyId: companyId,
                status: newStatus
            };
        }
        // --------------------------------------------------------------------

        try {
            const response = await fetch(STATUS_UPDATE_API_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update status.');
            }

            onStatusChange(item.userId, newStatus, itemType); // Use userId for users to find in state
            showNotification(`Status for '${itemName}' updated to '${newStatus}'.`, 'success');

        } catch (error) {
            console.error(`Error updating ${itemType} status:`, error);
            setStatus(initialStatus);
            showNotification(`Update for '${itemName}' failed: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const options = ['Pending', 'Approved', 'Rejected'];
    return <select value={status} onChange={(e) => handleStatusChange(e.target.value)} disabled={loading} className="p-2 border rounded bg-white">{options.map(o => <option key={o} value={o}>{o}</option>)}</select>;
};

const AdminView = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

    const showNotification = (message, type = 'success') => {
        setNotification({ show: true, message, type });
        setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 5000);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // The backend checks that the signed-in user is an admin before returning anything.
                setData(await getAdminData());
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleStatusChange = (id, newStatus, itemType) => {
        setData(prevData => {
            return prevData.map(item => {
                // For users, we must use the unique userId to find them in the local state array
                const itemId = itemType === 'User' ? item.userId : item.companyId;
                if (item.itemType === itemType && itemId === id) {
                    const updatedItem = { ...item };
                    if (itemType === 'Company') {
                        updatedItem.companyStatus = newStatus;
                    } else {
                        updatedItem.userApprovalStatus = newStatus;
                    }
                    return updatedItem;
                }
                return item;
            });
        });
    };

    const renderTable = (title, items, columns, itemType) => (
        <div className="mb-8">
            <h2 className="mb-4 text-xl font-bold">{title}</h2>
            <div className="overflow-x-auto rounded-lg bg-gray-100 p-4 shadow-md">
                <table className="min-w-full bg-white">
                    <thead><tr>{columns.map(c => <th key={c.key} className="px-6 py-3 border-b-2 border-gray-300 text-left leading-4 text-blue-500 tracking-wider">{c.label}</th>)}</tr></thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={item.PK + item.SK}>
                                {columns.map(col => (
                                    <td key={col.key} className="px-6 py-4 whitespace-no-wrap border-b border-gray-500">
                                        {col.key === 'companyStatus' || col.key === 'userApprovalStatus' ? (
                                            <StatusDropdown item={item} itemType={itemType} onStatusChange={handleStatusChange} showNotification={showNotification} />
                                        ) : (item[col.key])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    if (loading) return <div className="min-h-screen"><Header /><main className="mx-auto max-w-screen-lg p-4"><p>Loading data...</p></main><Footer /></div>;
    if (error) return <div className="min-h-screen"><Header /><main className="mx-auto max-w-screen-lg p-4"><p>Error: {error}</p></main><Footer /></div>;

    // Filter items based on their SK to differentiate Companies from Users
    const companies = data.filter(item => item.SK === 'METADATA');
    const users = data.filter(item => item.SK.startsWith('USER#'));

    const companyColumns = [ { key: 'companyName', label: 'Company Name' }, { key: 'companyId', label: 'Company ID' }, { key: 'companyStatus', label: 'Status' }, { key: 'createdAt', label: 'Created At' } ];
    const userColumns = [ { key: 'fullName', label: 'Full Name' }, { key: 'email', label: 'Email' }, { key: 'userId', label: 'User ID' }, { key: 'userApprovalStatus', label: 'Approval Status' }, { key: 'isFirstLogin', label: 'First Login' } ];

    return (
        <div className='min-h-screen'>
            <Header />
            {notification.show && <Notification message={notification.message} type={notification.type} onClose={() => setNotification({ ...notification, show: false })} />}
            <main className="mx-auto max-w-screen-lg p-4">
                <h1 className="mb-4 text-2xl font-bold">Admin View - Company and User Data</h1>
                {renderTable('Companies', companies, companyColumns, 'Company')}
                {renderTable('Users', users, userColumns, 'User')}
            </main>
            <Footer />
        </div>
    );
}

export default AdminView;